import { CircularProgress, Stack, Typography } from '@ace-clublabs/shared-component-library';
import { numberHelper } from '../../utils/numberHelper';

interface PriceInfoCardProps {
  originalPrice?: number;
  priceAfterDiscount?: number;
  priceRange?: { min: number; max: number };
  priceSuffix?: string;
  className?: string;
  isPriceCartLoading?: boolean;
  isUpdating?: boolean;
}

export const PriceInfoCard = ({
  originalPrice,
  priceAfterDiscount,
  priceSuffix,
  priceRange,
  className,
  isPriceCartLoading,
  isUpdating,
}: PriceInfoCardProps) => {
  const isFree = priceAfterDiscount === 0;
  const hasDiscount =
    priceAfterDiscount !== undefined && originalPrice !== undefined && priceAfterDiscount < originalPrice;

  return isUpdating ? (
    <CircularProgress
      variant="indeterminate"
      size={20}
      className="price-loading-spinner"
      sx={{
        marginRight: '0.2rem',
        lineHeight: '1rem',
        color: '#6C7175',
        '& circle': { stroke: '#6C7175' },
      }}
    />
  ) : (
    <>
      <Stack direction="row" alignSelf="flex-start" alignItems="baseline" sx={{ gap: '0.5rem', lineHeight: '1.75rem' }}>
        {isFree && (
          <Typography
            variant="b1Medium"
            className={`free-price-label-${className}`}
            sx={{ whiteSpace: 'nowrap', color: isPriceCartLoading ? '#CACDCE' : '#7527B9' }}
          >
            {className === 'family-upgrade-fee' ? 'Waived' : 'Free'}
          </Typography>
        )}
        {!isFree && hasDiscount && priceAfterDiscount !== undefined && (
          <Typography
            sx={{ display: 'flex', alignItems: 'center', color: isPriceCartLoading ? '#CACDCE' : '#7527B9' }}
            className={`discounted-price-${className}`}
            variant="b1Medium"
          >
            {numberHelper.formatMoney(priceAfterDiscount)}
          </Typography>
        )}
        {/*This item is for displaying the price range for associates costs*/}
        {priceRange !== undefined && (
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'center',
              ...(isPriceCartLoading
                ? { color: '#CACDCE' }
                : hasDiscount || isFree
                  ? { color: '#6C7175', textDecoration: 'line-through' }
                  : {}),
            }}
            className={`original-cost-${className}`}
            variant={hasDiscount || isFree ? 'b2Regular' : 'b1Medium'}
          >
            {numberHelper.formatMoney(priceRange.min)}-{numberHelper.formatMoney(priceRange.max, true)}
            <Typography
              variant="b2Regular"
              className={`recurring-period-label-${className}`}
              sx={{
                whiteSpace: 'nowrap',
                textDecoration: 'inherit',
                color: isPriceCartLoading ? '#CACDCE' : '#6C7175',
                marginLeft: hasDiscount || isFree ? 0 : '0.25rem',
              }}
            >
              /{priceSuffix}
            </Typography>
          </Typography>
        )}
        {originalPrice !== undefined && (
          <Typography
            sx={{
              display: 'flex',
              alignItems: 'baseline',
              ...(isPriceCartLoading
                ? { color: '#CACDCE' }
                : hasDiscount && { color: '#6C7175', textDecoration: 'line-through' }),
            }}
            className={`original-cost-${className}`}
            variant={hasDiscount ? 'b2Regular' : 'b1Medium'}
          >
            {numberHelper.formatMoney(originalPrice)}
            {priceSuffix && (
              <Typography
                variant="b2Regular"
                className={`recurring-period-label-${className}`}
                sx={{
                  whiteSpace: 'nowrap',
                  textDecoration: 'inherit',
                  color: isPriceCartLoading ? '#CACDCE' : '#6C7175',
                  marginLeft: hasDiscount ? 0 : '0.25rem',
                }}
              >
                /{priceSuffix}
              </Typography>
            )}
          </Typography>
        )}
      </Stack>
    </>
  );
};
